/**
 * The dock's wiring: one day out of the document, the catalogue that names what it trains, and the
 * view model that falls out of the two.
 *
 * Decides nothing. Every rule it appears to apply lives in `entities/plan-draft` or in this slice's
 * `model/dayProgramming.ts`, which is why `DayForm` can be read without opening this file.
 *
 * ── Three narrow subscriptions, never the tree ───────────────────────────────────────────────
 * `useDayDraft` returns the ONE day node, whose identity Immer's structural sharing preserves across
 * every edit that did not touch it — so retyping a weight in Wednesday does not re-render a dock
 * open on Monday. The two neighbour tokens are strings. None of the three can return a fresh object,
 * which matters more than it looks: zustand reads a selector inside `useSyncExternalStore`'s
 * `getSnapshot`, and a selector minting a new value on every call reports a changed snapshot forever.
 *
 * ── The catalogue is read here rather than passed in ─────────────────────────────────────────
 * Through `useExerciseCatalogue`, the same cache entry the week canvas holds; taking it as a prop
 * would put a `ReadonlyMap` on the feature's interface that every host would have to build. An
 * exercise the attribution does not carry contributes nothing rather than zero, so a catalogue that
 * has not arrived leaves the muscle bars absent and every parameter editable.
 */

import { useTranslation } from 'react-i18next';

import {
  useAdjacentDayToken,
  useDayDraft,
  useDayPhaseCycleCount,
  useDayPhaseStartCycle,
} from '~/entities/plan-draft';

import { dayProgrammingView, type DayProgrammingView, type Translate } from '../model/dayProgramming';

import { useExerciseCatalogue } from './useExerciseCatalogue';

export interface DayProgrammingState {
  /** `null` when the token names no day — a stale `?day=`, or a day removed in another tab. */
  readonly view: DayProgrammingView | null;
  readonly previousDayToken: string | null;
  readonly nextDayToken: string | null;
  readonly t: Translate;
}

/**
 * @param cycleIndex The ABSOLUTE cycle the form should render, or `null` for the authored base.
 *   The calendar's day editor passes the open session's cycle; the builder passes nothing.
 *
 *   The phase's own span is read HERE rather than taken as a prop, because it is a fact about the
 *   document and the caller holding it would have to walk the tree to find it. Two scalars rather
 *   than one window object, for the reason the store's own header gives at length: a selector
 *   minting a fresh object inside `getSnapshot` re-renders forever.
 *
 * @param options.libraryIsModal Whether this host's library lies OVER the day. The panel's does, so
 *   its two "+" pills drop the clause about dragging a row in — the form behind a modal drawer is
 *   `inert` and the drop lands on nothing.
 */
export function useDayProgramming(
  daySlotToken: string | null,
  cycleIndex: number | null = null,
  options: { readonly libraryIsModal?: boolean } = {},
): DayProgrammingState {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const day = useDayDraft(daySlotToken);
  const previousDayToken = useAdjacentDayToken(daySlotToken, 'previous');
  const nextDayToken = useAdjacentDayToken(daySlotToken, 'next');
  const phaseStartCycle = useDayPhaseStartCycle(daySlotToken);
  const phaseCycleCount = useDayPhaseCycleCount(daySlotToken);

  const catalogue = useExerciseCatalogue();

  return {
    view:
      day === null
        ? null
        : dayProgrammingView({
            day,
            ...catalogue,
            t,
            locale,
            // All three or none: a cycle with no phase behind it has no origin to project from, so
            // the form falls back to the authored base rather than to a guessed window.
            cycle:
              cycleIndex === null || phaseStartCycle === null || phaseCycleCount === null
                ? null
                : { cycleIndex, phaseStartCycle, phaseCycleCount },
            // The builder's panel adds from a drawer laid OVER the day, so the two "+" pills there
            // must not offer a drag the modality makes impossible. See `DropZoneView`.
            libraryIsModal: options.libraryIsModal ?? false,
          }),
    previousDayToken,
    nextDayToken,
    t,
  };
}
